'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Record } from 'immutable';
import Raven from 'raven-js';
import { Networks } from '../../lib/Constants'; // AdsBase/src/main/java/com/hubspot/ads/data/tracking/TrackingErrorReason.java

const NetworklessErrors = {
  UNKNOWN: 'UNKNOWN',
  API_ERROR: 'API_ERROR',
  NETWORK_UNAVAILABLE: 'NETWORK_UNAVAILABLE',
  TYPE_UNSUPPORTED_BY_API: 'TYPE_UNSUPPORTED_BY_API',
  TYPE_NOT_TRACKABLE: 'TYPE_NOT_TRACKABLE',
  TYPE_UNKNOWN: 'TYPE_UNKNOWN',
  UNSUPPORTED_CTA: 'UNSUPPORTED_CTA',
  NO_URLS_FOUND: 'NO_URLS_FOUND',
  UNABLE_TO_PARSE_URL: 'UNABLE_TO_PARSE_URL',
  NO_REDIRECT: 'NO_REDIRECT',
  TOO_MANY_REDIRECTS: 'TOO_MANY_REDIRECTS',
  NO_PERMISSION: 'NO_PERMISSION',
  NO_PAGE_PERMISSION: 'NO_PAGE_PERMISSION',
  ENTITY_COMPLETED: 'ENTITY_COMPLETED',
  ENTITY_DELETED: 'ENTITY_DELETED',
  TWO_FACTOR: 'TWO_FACTOR',
  INVALID_POST_BODY: 'INVALID_POST_BODY',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  CREATIVE_UNSUPPORTED_BY_API: 'CREATIVE_UNSUPPORTED_BY_API'
};
const FacebookErrors = {
  FB_INTERNAL_LINKS: 'FB_INTERNAL_LINKS',
  FB_INVALID_IMAGE_CROP: 'FB_INVALID_IMAGE_CROP',
  FB_INSTAGRAM_STORY_ENGAGEMENTS: 'FB_INSTAGRAM_STORY_ENGAGEMENTS',
  FB_TOO_MANY_ENGAGEMENTS: 'FB_TOO_MANY_ENGAGEMENTS',
  FB_CUSTOM_AUDIENCE_TERMS: 'FB_CUSTOM_AUDIENCE_TERMS',
  FB_INVALID_PARAMETERS: 'FB_INVALID_PARAMETERS',
  FB_INVALID_TARGETING: 'FB_INVALID_TARGETING',
  FB_INVALID_NEGATIVE_TARGETING: 'FB_INVALID_NEGATIVE_TARGETING',
  FB_UNSUPPORTED_AD: 'FB_UNSUPPORTED_AD',
  FB_DUPLICATE_USER_OR_MEDIA_OBJECT: 'FB_DUPLICATE_USER_OR_MEDIA_OBJECT',
  FB_ASSET_LABEL_ISSUE: 'FB_ASSET_LABEL_ISSUE',
  FB_DUPLICATE_AD_ASSET: 'FB_DUPLICATE_AD_ASSET',
  FB_NO_PERMISSION: 'FB_NO_PERMISSION',
  FB_NETWORK_UNAVAILABLE: 'FB_NETWORK_UNAVAILABLE',
  FB_PLACEMENT_INELIGIBLE_FOR_ADVANTAGE_PLUS: 'FB_PLACEMENT_INELIGIBLE_FOR_ADVANTAGE_PLUS'
};
const GoogleErrors = {
  GOOGLE_EDITED_TEMPLATE: 'GOOGLE_EDITED_TEMPLATE',
  GOOGLE_INVALID_POST_BODY: 'GOOGLE_INVALID_POST_BODY'
};
const LinkedInErrors = {
  LI_SHARE_NOT_PRESENT: 'LI_SHARE_NOT_PRESENT',
  LI_UNABLE_TO_FETCH_UGC: 'LI_UNABLE_TO_FETCH_UGC',
  LI_NO_SHARE_SPECIFIC_CONTENT: 'LI_NO_SHARE_SPECIFIC_CONTENT',
  LI_MEDIA_NOT_PRESENT: 'LI_MEDIA_NOT_PRESENT',
  LI_SHARED_CONTENT_EDITED_TRACKING: 'LI_SHARED_CONTENT_EDITED_TRACKING',
  LI_UNABLE_TO_RESOLVE_SHORT_LINK: 'LI_UNABLE_TO_RESOLVE_SHORT_LINK',
  LI_NO_PERMISSION: 'LI_NO_PERMISSION',
  LI_CREATIVE_POST_NOT_PRESENT: 'LI_CREATIVE_POST_NOT_PRESENT',
  LI_UNABLE_TO_FETCH_POST: 'LI_UNABLE_TO_FETCH_POST',
  LI_CREATIVE_CONTENT_NOT_PRESENT: 'LI_CREATIVE_CONTENT_NOT_PRESENT'
};
export const TrackingErrorReasons = Object.assign({}, NetworklessErrors, {}, FacebookErrors, {}, GoogleErrors, {}, LinkedInErrors);
export const TRACKING_ERROR_NETWORK_MAPPER = {
  [NetworklessErrors.NO_PERMISSION]: {
    [Networks.FACEBOOK]: FacebookErrors.FB_NO_PERMISSION,
    [Networks.LINKEDIN]: LinkedInErrors.LI_NO_PERMISSION
  },
  [NetworklessErrors.NETWORK_UNAVAILABLE]: {
    [Networks.FACEBOOK]: FacebookErrors.FB_NETWORK_UNAVAILABLE
  },
  [TrackingErrorReasons.INVALID_POST_BODY]: {
    [Networks.ADWORDS]: GoogleErrors.GOOGLE_INVALID_POST_BODY
  }
};
export const IGNORABLE_TRACKING_ERRORS = [TrackingErrorReasons.ENTITY_COMPLETED, TrackingErrorReasons.ENTITY_DELETED];
export const MAP_TO_UNKNOWN = [TrackingErrorReasons.INTERNAL_ERROR, TrackingErrorReasons.API_ERROR, TrackingErrorReasons.FB_INVALID_PARAMETERS, // This hasn't been seen since Feb '20
// We mapped these to OTHER in the old object
TrackingErrorReasons.LI_SHARE_NOT_PRESENT, TrackingErrorReasons.LI_UNABLE_TO_FETCH_UGC, TrackingErrorReasons.LI_NO_SHARE_SPECIFIC_CONTENT, TrackingErrorReasons.LI_MEDIA_NOT_PRESENT, // New tracking errors without information on how to fix them
TrackingErrorReasons.LI_CREATIVE_POST_NOT_PRESENT, TrackingErrorReasons.LI_UNABLE_TO_FETCH_POST, TrackingErrorReasons.LI_CREATIVE_CONTENT_NOT_PRESENT, TrackingErrorReasons.FB_PLACEMENT_INELIGIBLE_FOR_ADVANTAGE_PLUS];

class TrackingErrorRecord extends Record({
  description: null,
  dismissedAt: null,
  dismissedBy: null,
  firstOccurredAt: null,
  id: null,
  tkt: null,
  lastOccurredAt: null,
  reason: null,
  resolvedAt: null
}) {
  constructor(_ref) {
    let {
      reason
    } = _ref,
        extra = _objectWithoutPropertiesLoose(_ref, ["reason"]);

    if (!Object.values(TrackingErrorReasons).includes(reason)) {
      Raven.captureMessage(`Unexpected tracking error reason: ${reason}`, {
        extra
      });
      super(Object.assign({
        reason: TrackingErrorReasons.UNKNOWN
      }, extra));
      return;
    }

    if (MAP_TO_UNKNOWN.includes(reason)) {
      super(Object.assign({
        reason: TrackingErrorReasons.UNKNOWN
      }, extra));
      return;
    }

    super(Object.assign({
      reason
    }, extra));
  }

}

export default TrackingErrorRecord;